import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PortfolioForm({ item, onSave, onCancel, defaultOrder }) {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    category: item?.category || '',
    description: item?.description || '',
    image: item?.image || '',
    details: item?.details || '',
    order: item?.order || defaultOrder
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white">{item ? 'Edit' : 'Add'} Portfolio Item</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-gray-700 border-gray-600 text-white" required />
            </div>
            <div>
              <Label className="text-gray-300">Order</Label>
              <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} className="bg-gray-700 border-gray-600 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-gray-300">Category/Service</Label>
            <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="bg-gray-700 border-gray-600 text-white" required />
          </div>
          <div>
            <Label className="text-gray-300">Short Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="bg-gray-700 border-gray-600 text-white" required />
          </div>
          <div>
            <Label className="text-gray-300">Details</Label>
            <Textarea value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} rows={3} className="bg-gray-700 border-gray-600 text-white" required />
          </div>
          <div>
            <Label className="text-gray-300">Image URL</Label>
            <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} className="bg-gray-700 border-gray-600 text-white" />
            {formData.image && <img src={formData.image} alt="Preview" className="w-32 h-24 object-cover rounded mt-2" />}
          </div>
          <div className="flex gap-4 pt-4 border-t border-gray-700">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-gray-600">Cancel</Button>
            <Button type="submit" className="flex-1 bg-[#F54021] hover:bg-[#ff5a35]">{item ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PortfolioCard({ item, onEdit, onDelete }) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="relative">
        <img src={item.image} alt={item.title} className="w-full h-40 object-cover" />
        <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">#{item.order}</span>
      </div>
      <div className="p-4">
        <span className="text-xs text-[#F54021] uppercase">{item.category}</span>
        <h3 className="text-white font-bold mt-1">{item.title}</h3>
        <p className="text-gray-400 text-sm mt-1 line-clamp-2">{item.description}</p>
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="outline" onClick={() => onEdit(item)} className="flex-1 border-gray-600"><Pencil className="w-4 h-4 mr-1" />Edit</Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(item.id)} className="border-red-600 text-red-400 hover:bg-red-900/30"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/portfolio`);
      setItems(response.data);
    } catch (error) { toast.error('Failed to fetch'); }
    finally { setLoading(false); }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/portfolio/${editingItem.id}`, data);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/portfolio`, data);
        toast.success('Created');
      }
      fetchItems();
      setShowForm(false);
      setEditingItem(null);
    } catch (error) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (error) { toast.error('Failed'); }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  const sortedItems = [...items].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Portfolio</h2>
        <Button onClick={() => { setEditingItem(null); setShowForm(true); }} className="bg-[#F54021] hover:bg-[#ff5a35]"><Plus className="w-4 h-4 mr-2" />Add</Button>
      </div>
      {showForm && <PortfolioForm item={editingItem} onSave={handleSave} onCancel={() => { setShowForm(false); setEditingItem(null); }} defaultOrder={items.length + 1} />}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedItems.map((item) => <PortfolioCard key={item.id} item={item} onEdit={(i) => { setEditingItem(i); setShowForm(true); }} onDelete={handleDelete} />)}
      </div>
      {items.length === 0 && <div className="text-center py-12 text-gray-400">No portfolio items yet.</div>}
    </div>
  );
}
